// mds-dojo — small OPT-IN helpers for widgets that want to be fully HMR-friendly.
//
// The state-preserving HMR plugin (mds-dojo/vite/mds-hmr.mjs) hot-swaps a widget's
// METHOD BODIES on the live instances with no cooperation needed. Two app patterns,
// though, need a tiny opt-in to survive a hot-swap cleanly — these helpers cover them.
// Both are BUILD-SAFE: when `import.meta.hot` is absent (a production build) they
// degrade to ordinary behaviour, so it is safe to use them in shipped widgets.

/**
 * Preserve a MODULE-LEVEL value across HMR re-evaluations.
 *
 * A Vite hot update RE-RUNS the module, so `let editor = null` at module scope is
 * reset to null even though a live instance is still on the page — the classic shared-
 * singleton hazard (the `SolvedSingletonLite` exemplar in HmrSolved/). `hmrKeep` returns a stable HOLDER whose `.current`
 * survives the re-eval (stored in `import.meta.hot.data`), so mutate `.current`, never
 * a bare `let`:
 *
 * ```ts
 * const editor = hmrKeep('sharedEditor', () => null);   // module scope
 * // …later…  editor.current = new SharedEditor();      // survives hot updates
 * ```
 *
 * In a production build (`import.meta.hot` undefined) it just returns a fresh holder —
 * the module still evaluates once, so the singleton semantics are identical.
 */
export function hmrKeep<T>(key: string, init: () => T): { current: T } {
  const hot: any = (import.meta as any).hot;
  if (!hot) { return { current: init() }; }
  if (!hot.data[key]) { hot.data[key] = { current: init() }; }
  // Carry the holder forward to the next module instance (Vite hands the disposed
  // `data` object to the updated module as its `hot.data`). Re-registered every eval.
  hot.dispose((data: any) => { data[key] = hot.data[key]; });
  return hot.data[key];
}

/**
 * Late-bind an event handler to a method NAME so a hot-patch of that method is picked
 * up without re-wiring.
 *
 * A handler wired by VALUE — `on(node, 'click', this._onClick.bind(this))` or
 * `lang.hitch(this, this._onClick)` — captures the *current* function; after a
 * hot-patch the node still calls the old one. `hmrBind` resolves `obj[name]` at CALL
 * time instead, so the patched method runs on the next event:
 *
 * ```ts
 * on(node, 'click', hmrBind(this, '_onClick', slot, data));
 * ```
 *
 * (Widgets wired through `data-dojo-attach-event` are already late-bound and need
 * nothing.) Behaviour is identical in a production build.
 */
export function hmrBind(obj: any, methodName: string, ...pre: any[]): (...args: any[]) => any {
  return function (this: any, ...args: any[]): any {
    const fn = obj[methodName];
    if (typeof fn !== 'function') { throw new Error(`hmrBind: ${methodName} is not a method`); }
    return fn.apply(obj, pre.length ? pre.concat(args) : args);
  };
}
